import { Typography, CircularProgress, Alert, Box, Button, Snackbar } from "@mui/material";
import ReportCard from "./ReportCard";
import { useReportDownload, useReports } from "../../hooks/useReports";
import {  getInputParamsFromApi, useUserInfoSchema } from "../../state/services";
import { useOktaAuth } from "@okta/okta-react";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useInputParamState } from "../../state/Input-param-state";
import { addDays } from "date-fns";
import DateRangeSelector, {
  RangeValue ,
} from "../../components/DateRangePicker";
import { ReportItem } from "../../api-request/api-request";

export default function Dashboard() {
  const userInfo = useUserInfoSchema();
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken || '';
  const [enabled, setEnabled] = useState(false)
  const [shouldLoadParams, setShouldLoadParams] = useState(false)
  const paramState = useInputParamState()
  const [parameterValues, setParameterValues] = useState<
    Record<string, Record<string, string>>
  >({});

  const handleParameterChange = (
    reportCode: string,
    parameterCode: string,
    value: string
  ) => {
    setParameterValues((prev) => ({
      ...prev,
      [reportCode]: {
        ...prev[reportCode],
        [parameterCode]: value,
      },
    }));
  };

  const today = new Date();
  const [range, setRange] = useState<RangeValue>({
    from: addDays(today, -6),
    to: today,
  });

  const { downloadingCode, downloadError, setDownloadError, handleDownload } =
    useReportDownload(accessToken);

  useEffect(() => {
    if (!shouldLoadParams) return;

    if (Object.keys(userInfo).length > 0) {
      setEnabled(true)
      return
    }

    getInputParamsFromApi(paramState, accessToken);
    setEnabled(true)
  }, [shouldLoadParams]);

  useEffect(() => {
    setShouldLoadParams(true)
  }, [])

  const canAccess = userInfo?.user_group === "admin";

  const { reports, loading, error } = useReports(enabled, accessToken, canAccess);

  if (!userInfo || !canAccess) {
    return <AccessDenied />;
  }

  const handleDownloadFile = async (item: ReportItem) => {
    await handleDownload({
      item,
      range,
      parameterValues: parameterValues[item.code] || {},
    });
  };

return (
  <div className="px-4 py-4 mt-6 sm:px-6 sm:py-6 sm:mt-10 overflow-y-auto">
    <Snackbar
      open={!!downloadError}
      autoHideDuration={4000}
      onClose={() => setDownloadError(null)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert severity="error" onClose={() => setDownloadError(null)}>
        {downloadError}
      </Alert>
    </Snackbar>

    {loading && (
      <div className="flex justify-center py-8 sm:py-10">
        <CircularProgress />
      </div>
    )}

    {error && (
      <Alert severity="error" className="mb-4">
        {error}
      </Alert>
    )}

    {!loading && !error && reports.length === 0 && (
      <Alert severity="info">No reports found.</Alert>
    )}

    {!loading && !error && reports.length > 0 && (
      <>
        {reports.length > 0 && (
          <section className="mb-8 mt-2">
            <Box className="mb-4 sm:mb-6">
              <div className="w-full max-w-xl rounded-xl bg-white p-3 sm:p-6">
                <DateRangeSelector value={range} onChange={setRange} />
              </div>

              <Typography
                variant="body1"
                className="mt-2 text-sm sm:text-base text-gray-500"
              >
                Available reports with date range.
              </Typography>
            </Box>

            <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 xl:grid-cols-3">
              {reports.map((report) => (
                <ReportCard
                  key={report.code}
                  report={report}
                  downloading={downloadingCode === report.code}
                  onDownload={handleDownloadFile}
                  parameterValues={parameterValues[report.code] || {}}
                  onParameterChange={handleParameterChange}
                />
              ))}
            </div>
          </section>
        )}
      </>
    )}
  </div>
);
}

const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <Box className="flex flex-col items-center justify-center h-[70vh] text-center">
      <LockIcon sx={{ fontSize: 80, color: "#9ca3af" }} />

      <Typography variant="h4" className="mt-4 font-semibold">
        Access Denied
      </Typography>

      <Typography variant="body1" className="mt-2 text-gray-500">
        You don't have permission to access this page.
      </Typography>

      <Button
        variant="outlined"
        className="mt-6"
        onClick={() => navigate("/")}
      >
        Go to Home
      </Button>
    </Box>
  );
};
